import { MihatoMark } from "@/common/components/MihatoMark";
import { useDismiss } from "@/common/hooks/useDismiss";
import { ApiError } from "@/common/lib/api";
import type { AccountingFramework } from "@/features/accounts/api/accountsApi";
import { useCreateAccount, useMe } from "@/features/accounts/api/accountsApi";
import { writeCurrentAccountId } from "@/features/accounts/hooks/useCurrentAccount";
import { ChevronDown } from "lucide-react";
import { type FormEvent, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

const COUNTRY_OPTIONS = [{ value: "CR", label: "Costa Rica" }];

const FRAMEWORK_OPTIONS: { value: AccountingFramework; label: string; hint: string }[] = [
  {
    value: "pymes_s34",
    label: "NIIF para PYMES — Sección 34",
    hint: "",
  },
  {
    value: "ifrs_full",
    label: "NIIF plenas",
    hint: "(requiere auditor)",
  },
];

export function CreateAccountPage() {
  const navigate = useNavigate();
  const { data: me, isLoading: meLoading } = useMe();
  const mutation = useCreateAccount();

  const [name, setName] = useState("");
  const [countryCode, setCountryCode] = useState("CR");
  const [framework, setFramework] = useState<AccountingFramework>("pymes_s34");
  const [countryOpen, setCountryOpen] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const countrySelectorRef = useRef<HTMLDivElement>(null);

  useDismiss({
    enabled: countryOpen,
    containerRef: countrySelectorRef,
    onDismiss: () => setCountryOpen(false),
  });

  const activeMembershipsCount = me?.memberships.filter((m) => m.status === "active").length ?? 0;
  const isFirstAccount = activeMembershipsCount === 0;

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError("El nombre es obligatorio.");
      return;
    }
    if (trimmedName.length > 100) {
      setNameError("El nombre no puede superar los 100 caracteres.");
      return;
    }
    setNameError(null);

    try {
      const result = await mutation.mutateAsync({
        name: trimmedName,
        countryCode,
        accountingFramework: framework,
      });
      writeCurrentAccountId(result.account.id);
      navigate("/app", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.code === "MAX_ACCOUNTS_PER_USER") {
        toast.error("Llegaste al límite de cuentas (50). Si necesitás más, contactá soporte.");
      } else if (err instanceof ApiError && err.status === 400) {
        toast.error("Revisá los datos del formulario e intentá de nuevo.");
      } else {
        toast.error("No se pudo crear la cuenta. Intentá de nuevo.");
      }
    }
  }

  const selectedCountryLabel =
    COUNTRY_OPTIONS.find((o) => o.value === countryCode)?.label ?? "Seleccionar";

  return (
    <>
      <title>Crear cuenta · HATO</title>
      <meta name="robots" content="noindex, nofollow" />

      <div
        className="landing-texture pointer-events-none fixed inset-0 opacity-50"
        aria-hidden="true"
      />

      <main className="relative min-h-screen bg-brand-dark text-white">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-[90vh]"
          style={{
            background:
              "radial-gradient(circle at 50% 10%, rgba(0, 229, 153, 0.08) 0%, transparent 60%)",
          }}
        />

        <div className="relative mx-auto flex min-h-screen w-full max-w-md flex-col justify-center px-6 py-12">
          <Link to="/" className="mb-10 flex items-center gap-3 self-start">
            <MihatoMark className="h-7 w-7 text-white" />
            <span className="font-medium text-lg tracking-tight">HATO</span>
          </Link>

          <div className="mb-8 flex flex-col gap-2">
            <h1 className="font-medium text-3xl text-white tracking-tight md:text-4xl">
              {isFirstAccount ? "Bienvenido a Mihato" : "Crear nueva cuenta"}
            </h1>
            <p className="text-neutral-400 text-sm leading-relaxed">
              {isFirstAccount
                ? "Para empezar, creá tu primera cuenta. Si te invitaron a una cuenta existente, buscá el email de invitación."
                : "Cada cuenta es una finca u organización independiente, con su propio hato, miembros y reportes."}
            </p>
          </div>

          <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
            {/* Nombre */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="account-name" className="font-medium text-neutral-300 text-sm">
                Nombre de la finca / organización{" "}
                <span className="text-rose-400" aria-hidden="true">
                  *
                </span>
              </label>
              <input
                id="account-name"
                name="name"
                type="text"
                required
                maxLength={100}
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (nameError) setNameError(null);
                }}
                placeholder="Finca El Roble"
                aria-invalid={Boolean(nameError)}
                className="h-12 w-full rounded-xl border border-white/[0.08] bg-brand-dark px-4 text-base text-white placeholder:text-neutral-500 focus:border-primary-500/60 focus:outline-none focus:ring-1 focus:ring-primary-500/40"
                disabled={mutation.isPending}
              />
              {nameError && (
                <p role="alert" className="text-rose-400 text-xs">
                  {nameError}
                </p>
              )}
            </div>

            {/* País */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="country-picker" className="font-medium text-neutral-300 text-sm">
                País
              </label>
              <div ref={countrySelectorRef} className="relative">
                <button
                  id="country-picker"
                  type="button"
                  onClick={() => setCountryOpen((o) => !o)}
                  disabled={mutation.isPending}
                  className="flex h-12 w-full items-center justify-between rounded-xl border border-white/[0.08] bg-brand-dark px-4 text-base text-white focus:outline-none"
                  aria-haspopup="listbox"
                  aria-expanded={countryOpen}
                  aria-label="País"
                >
                  <span>{selectedCountryLabel}</span>
                  <ChevronDown className="h-4 w-4 text-neutral-400" />
                </button>
                {countryOpen && (
                  <ul className="absolute top-full right-0 left-0 z-20 mt-1 overflow-hidden rounded-xl border border-white/[0.08] bg-brand-panel shadow">
                    {COUNTRY_OPTIONS.map((opt) => (
                      <li
                        key={opt.value}
                        aria-selected={opt.value === countryCode}
                        onClick={() => {
                          setCountryCode(opt.value);
                          setCountryOpen(false);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            setCountryCode(opt.value);
                            setCountryOpen(false);
                          }
                        }}
                        className={`cursor-pointer px-4 py-3 text-base transition-colors hover:bg-white/[0.05] ${
                          opt.value === countryCode ? "text-primary-400" : "text-white"
                        }`}
                      >
                        {opt.label}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Marco contable */}
            <div className="flex flex-col gap-2">
              <span className="font-medium text-neutral-300 text-sm">Marco contable</span>
              {FRAMEWORK_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className="flex cursor-pointer items-start gap-3 rounded-xl border border-white/[0.06] p-3 transition-colors hover:bg-white/[0.03]"
                >
                  <input
                    type="radio"
                    name="accounting_framework"
                    value={opt.value}
                    checked={framework === opt.value}
                    onChange={() => setFramework(opt.value)}
                    className="mt-0.5 accent-primary-500"
                    disabled={mutation.isPending}
                  />
                  <span className="text-sm text-white">
                    {opt.label}{" "}
                    {opt.hint && <span className="text-neutral-400 text-xs">{opt.hint}</span>}
                  </span>
                </label>
              ))}
            </div>

            <button
              type="submit"
              disabled={mutation.isPending || meLoading}
              className="mt-2 flex h-12 items-center justify-center rounded-full bg-primary-500 px-6 font-medium text-brand-dark text-sm transition-colors hover:bg-primary-400 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {mutation.isPending ? "Creando…" : "Crear cuenta"}
            </button>
          </form>
        </div>
      </main>
    </>
  );
}
